import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, Save, CheckCircle2, ArrowRight, MessageCircle, FileText, Award, Loader2 } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { startOrGetRun, saveDraft, advanceTask, setPortfolioVisibility } from "@/lib/api/lxp-runs.functions";
import { askMentor, listMentorMessages } from "@/lib/api/lxp-mentor.functions";
import { assessSubmission } from "@/lib/api/lxp-assess.functions";
import { completeSimulation } from "@/lib/api/lxp-certificate.functions";
import { generateLxpCertificatePdf } from "@/lib/api/lxp-certificate-pdf.functions";
import { downloadBase64File } from "@/lib/download-base64";

export const Route = createFileRoute("/_authenticated/lexpractiq/simulations/$slug/run")({
  head: () => ({ meta: [{ title: "Simulation Runner | SA LexPractiq" }] }),
  component: RunnerPage,
});

type Task = { key: string; title: string; brief: string; rubric?: string[] };
type Doc = { title: string; body: string };
type Msg = { id?: string; role: "user" | "mentor" | "system"; content: string };
type Sub = { id: string; task_index: number; body_html: string; ai_scores?: any; supervisor_narrative?: string; ai_feedback?: string; portfolio_visible?: boolean };

function RunnerPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const startFn = useServerFn(startOrGetRun);
  const saveFn = useServerFn(saveDraft);
  const advanceFn = useServerFn(advanceTask);
  const visFn = useServerFn(setPortfolioVisibility);
  const askFn = useServerFn(askMentor);
  const listMsgFn = useServerFn(listMentorMessages);
  const assessFn = useServerFn(assessSubmission);
  const completeFn = useServerFn(completeSimulation);
  const pdfFn = useServerFn(generateLxpCertificatePdf);

  const [state, setState] = useState<any>(null);
  const [taskIdx, setTaskIdx] = useState(0);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<any>(null);
  const [showComplete, setShowComplete] = useState(false);
  const [fullName, setFullName] = useState("");
  const [issuedCert, setIssuedCert] = useState<any>(null);

  const loadMessages = useCallback(async (runId: string, idx: number) => {
    const r = await listMsgFn({ data: { runId, taskIndex: idx } });
    setMessages(r.messages as any);
  }, [listMsgFn]);

  useEffect(() => {
    (async () => {
      try {
        const r: any = await startFn({ data: { slug } });
        setState(r);
        setTaskIdx(r.run.current_task_index || 0);
        const existing = r.submissions.find((s: Sub) => s.task_index === (r.run.current_task_index || 0));
        setDraft(existing?.body_html || "");
        if (existing?.ai_scores) setAssessment({ scores: existing.ai_scores, supervisor_narrative: existing.supervisor_narrative, revision_suggestions: (existing.ai_feedback || "").split("\n• ").filter(Boolean) });
        await loadMessages(r.run.id, r.run.current_task_index || 0);
      } catch (e: any) {
        toast.error(e?.message || "Failed to load simulation");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!state) {
    return <LxpLayout><div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}><Loader2 className="animate-spin" /></div></LxpLayout>;
  }

  const sim = state.simulation;
  const tasks = (sim.tasks || []) as Task[];
  const docs = (sim.document_pack || []) as Doc[];
  const task = tasks[taskIdx];
  const existingSub = (state.submissions || []).find((s: Sub) => s.task_index === taskIdx);
  const isLastTask = taskIdx === tasks.length - 1;

  async function save() {
    setBusy("save");
    try {
      await saveFn({ data: { runId: state.run.id, taskIndex: taskIdx, body: draft } });
      toast.success("Draft saved");
    } catch (e: any) { toast.error(e?.message || "Save failed"); }
    finally { setBusy(null); }
  }

  async function submit() {
    if (!draft.trim()) return toast.error("Draft something first");
    setBusy("submit");
    try {
      await saveFn({ data: { runId: state.run.id, taskIndex: taskIdx, body: draft } });
      const r: any = await assessFn({ data: { runId: state.run.id, taskIndex: taskIdx } });
      if (!r.ok) { toast.error(r.error); return; }
      setAssessment(r);
      toast.success(`Assessed! ${r.overall_percent}%  +${r.xp_awarded} XP`);
      // refresh state
      const s: any = await startFn({ data: { slug } });
      setState(s);
    } catch (e: any) { toast.error(e?.message || "Submit failed"); }
    finally { setBusy(null); }
  }

  async function goNext() {
    const next = taskIdx + 1;
    if (next < tasks.length) {
      await advanceFn({ data: { runId: state.run.id, nextIndex: next } });
      setTaskIdx(next);
      const existing = (state.submissions || []).find((s: Sub) => s.task_index === next);
      setDraft(existing?.body_html || "");
      setAssessment(null);
      await loadMessages(state.run.id, next);
    } else {
      setShowComplete(true);
    }
  }

  async function issueCertificate() {
    if (!fullName.trim()) return toast.error("Enter your full name");
    setBusy("cert");
    try {
      const r: any = await completeFn({ data: { runId: state.run.id, fullName: fullName.trim() } });
      setIssuedCert(r.certificate);
      toast.success("Certificate issued!");
      // Auto-download the branded PDF
      try {
        const pdf: any = await pdfFn({ data: { certificateId: r.certificate.id } });
        downloadBase64File(pdf.base64, pdf.filename);
      } catch (e: any) {
        toast.error(e?.message || "Certificate saved, but PDF failed to generate");
      }
    } catch (e: any) { toast.error(e?.message || "Failed"); }
    finally { setBusy(null); }
  }

  async function downloadCertificate() {
    if (!issuedCert) return;
    setBusy("pdf");
    try {
      const pdf: any = await pdfFn({ data: { certificateId: issuedCert.id } });
      downloadBase64File(pdf.base64, pdf.filename);
    } catch (e: any) { toast.error(e?.message || "Failed to generate PDF"); }
    finally { setBusy(null); }
  }

  async function ask() {
    if (!chatInput.trim()) return;
    const q = chatInput.trim();
    setChatInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy("mentor");
    try {
      const ctx = `Task: ${task.title}\nBrief: ${task.brief}\nMy current draft (excerpt): ${draft.slice(0, 1500)}`;
      const r: any = await askFn({ data: { runId: state.run.id, taskIndex: taskIdx, question: q, taskContext: ctx } });
      if (r.ok) setMessages((m) => [...m, { role: "mentor", content: r.reply }]);
      else toast.error(r.error);
    } catch (e: any) { toast.error(e?.message || "Mentor unavailable"); }
    finally { setBusy(null); }
  }

  async function togglePortfolio() {
    if (!existingSub) return;
    await visFn({ data: { submissionId: existingSub.id, visible: !existingSub.portfolio_visible } });
    const s: any = await startFn({ data: { slug } });
    setState(s);
    toast.success(existingSub.portfolio_visible ? "Hidden from portfolio" : "Added to portfolio");
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
          <div>
            <Link to="/lexpractiq/simulations/$slug" params={{ slug }} className="lxp-muted" style={{ fontSize: "0.85rem", textDecoration: "none" }}>
              ← Back to overview
            </Link>
            <h1 style={{ margin: "0.25rem 0", fontSize: "1.3rem" }}>{sim.title}</h1>
            <div className="lxp-muted" style={{ fontSize: "0.85rem" }}>
              Task {taskIdx + 1} of {tasks.length} · {state.run.xp_earned} XP earned
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {tasks.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 32, height: 4, borderRadius: 2,
                  background: i < taskIdx ? "var(--lxp-accent)" : i === taskIdx ? "linear-gradient(90deg, var(--lxp-accent), var(--lxp-border))" : "var(--lxp-border)",
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "1.25rem" }} className="lxp-runner-grid">
          <div style={{ display: "grid", gap: "1rem" }}>
            <div className="lxp-card">
              <span className="lxp-badge">{task.title}</span>
              <p style={{ margin: "0.75rem 0 0", lineHeight: 1.6, fontSize: "0.95rem" }}>{task.brief}</p>
              {task.rubric && (
                <>
                  <h4 style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--lxp-text-muted)", margin: "1rem 0 0.5rem" }}>You will be scored on</h4>
                  <ul style={{ paddingLeft: "1.25rem", margin: 0, fontSize: "0.85rem", lineHeight: 1.65 }} className="lxp-muted">
                    {task.rubric.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </>
              )}
            </div>

            <div className="lxp-card">
              <h3 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem" }}>Your draft</h3>
              <textarea
                className="lxp-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={16}
                placeholder="Write your answer here…"
                style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }}
              />
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                <button className="lxp-btn-ghost" onClick={save} disabled={busy !== null}>
                  <Save size={14} /> Save draft
                </button>
                <button className="lxp-btn" onClick={submit} disabled={busy !== null || !draft.trim()}>
                  {busy === "submit" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                  Submit for assessment
                </button>
                {existingSub?.ai_scores && (
                  <button className="lxp-btn-ghost" onClick={togglePortfolio}>
                    {existingSub.portfolio_visible ? "✓ In portfolio (click to hide)" : "Add to public portfolio"}
                  </button>
                )}
              </div>
            </div>

            {assessment && (
              <div className="lxp-card" style={{ borderColor: "var(--lxp-accent)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <h3 style={{ margin: 0, fontSize: "1rem" }}>Supervisor feedback</h3>
                  <span className="lxp-badge" style={{ fontSize: "0.85rem" }}>{assessment.overall_percent ?? assessment.scores?.overall_percent}%</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.5rem", marginBottom: "1rem" }}>
                  {["legal_accuracy", "reasoning", "structure", "communication", "professionalism"].map((k) => (
                    <div key={k} style={{ padding: "0.5rem", background: "var(--lxp-surface)", borderRadius: 8, textAlign: "center" }}>
                      <div style={{ fontSize: "0.7rem", color: "var(--lxp-text-muted)", textTransform: "capitalize" }}>{k.replace("_", " ")}</div>
                      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--lxp-accent)" }}>{assessment.scores?.[k] ?? 0}/5</div>
                    </div>
                  ))}
                </div>
                <p style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", lineHeight: 1.6, margin: "0 0 1rem" }}>{assessment.supervisor_narrative}</p>
                {assessment.revision_suggestions?.length > 0 && (
                  <>
                    <h4 style={{ fontSize: "0.85rem", margin: "0 0 0.5rem" }}>Suggested revisions</h4>
                    <ul style={{ paddingLeft: "1.25rem", margin: 0, fontSize: "0.85rem", lineHeight: 1.6 }} className="lxp-muted">
                      {assessment.revision_suggestions.map((s: string, i: number) => <li key={i}>{s}</li>)}
                    </ul>
                  </>
                )}
                <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                  <button className="lxp-btn" onClick={goNext}>
                    {isLastTask ? <><Award size={14} /> Finish simulation</> : <>Next task <ArrowRight size={14} /></>}
                  </button>
                </div>
              </div>
            )}

            <details className="lxp-card">
              <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: "0.9rem" }}>
                <FileText size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                Document pack ({docs.length})
              </summary>
              <div style={{ marginTop: "1rem", display: "grid", gap: "0.75rem" }}>
                {docs.map((d, i) => (
                  <div key={i} style={{ background: "var(--lxp-surface)", padding: "0.75rem", borderRadius: 8 }}>
                    <strong style={{ fontSize: "0.85rem" }}>{d.title}</strong>
                    <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", whiteSpace: "pre-wrap", lineHeight: 1.55, color: "var(--lxp-text-muted)" }}>{d.body}</p>
                  </div>
                ))}
              </div>
            </details>
          </div>

          <aside className="lxp-card" style={{ display: "flex", flexDirection: "column", height: "fit-content", position: "sticky", top: "5rem", maxHeight: "80vh" }}>
            <h3 style={{ margin: "0 0 0.75rem", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <MessageCircle size={16} style={{ color: "var(--lxp-accent)" }} /> AI Supervising Attorney
            </h3>
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "0.75rem", fontSize: "0.85rem", minHeight: 240 }}>
              {messages.length === 0 && (
                <p className="lxp-muted" style={{ fontSize: "0.85rem", margin: 0 }}>
                  Ask questions — I'll guide, not give answers. Try "What must I check for procedural fairness?"
                </p>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    padding: "0.5rem 0.75rem", borderRadius: 10, lineHeight: 1.55,
                    background: m.role === "user" ? "var(--lxp-surface)" : "var(--lxp-accent-glow)",
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "92%", whiteSpace: "pre-wrap",
                  }}
                >{m.content}</div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input
                className="lxp-input"
                placeholder="Ask your supervisor…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              />
              <button className="lxp-btn" onClick={ask} disabled={busy === "mentor" || !chatInput.trim()}>
                {busy === "mentor" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              </button>
            </div>
          </aside>
        </div>

        {showComplete && !issuedCert && (
          <div className="lxp-card" style={{ marginTop: "1.5rem", maxWidth: 520, marginInline: "auto", textAlign: "center" }}>
            <Award size={40} style={{ color: "var(--lxp-accent)" }} />
            <h2 style={{ margin: "0.75rem 0 0.5rem" }}>Simulation complete!</h2>
            <p className="lxp-muted" style={{ margin: "0 0 1.25rem" }}>Enter the full name to appear on your certificate.</p>
            <input className="lxp-input" placeholder="Full legal name" value={fullName} onChange={(e) => setFullName(e.target.value)} style={{ marginBottom: "0.75rem" }} />
            <button className="lxp-btn" onClick={issueCertificate} disabled={busy === "cert"}>
              {busy === "cert" ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              Issue my certificate
            </button>
          </div>
        )}

        {issuedCert && (
          <div className="lxp-card" style={{ marginTop: "1.5rem", maxWidth: 520, marginInline: "auto", textAlign: "center", borderColor: "var(--lxp-accent)" }}>
            <Award size={48} style={{ color: "var(--lxp-accent)" }} />
            <h2>Certificate issued 🎉</h2>
            <p className="lxp-muted">Certificate {issuedCert.certificate_number}</p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1rem", flexWrap: "wrap" }}>
              <button className="lxp-btn" onClick={downloadCertificate} disabled={busy === "pdf"}>
                {busy === "pdf" ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                Download PDF certificate
              </button>
              <Link to="/lexpractiq/verify/$token" params={{ token: issuedCert.verification_token }} className="lxp-btn-ghost">View verification</Link>
              <button className="lxp-btn-ghost" onClick={() => navigate({ to: "/lexpractiq/dashboard" })}>Go to dashboard</button>
            </div>
          </div>
        )}

        <style>{`@media (max-width: 900px){.lxp-runner-grid{grid-template-columns:1fr !important;}}`}</style>
      </div>
    </LxpLayout>
  );
}
